import type { MessageGeneration } from 'features/chat/types/chat';
import { shouldRenderSkillStreamStatus } from './parse-message-skill-events';
import { shouldRenderSubagentStreamStatus } from './parse-message-subagent-runs';
import { shouldRenderToolCallStreamStatus } from './parse-message-tool-calls';
import { hasStreamStatusEntries } from './stream-status-entries';

const METADATA_ONLY_STREAM_EVENT_TYPES = new Set([
  'MODEL_SELECTED',
  'RESPONSE_TIME_ANALYTICS',
  'ASSISTANT_REQUEST_PROCESSING_STARTED',
  'TOOL_CALL_STARTED',
  'TOOL_CALL_COMPLETED',
  'SKILL_USED',
  'SKILL_SKIPPED',
  'SKILL_RESOURCE_LOADED',
  'SUBAGENT_STARTED',
  'SUBAGENT_COMPLETED',
  'SUBAGENT.STARTED',
  'SUBAGENT.COMPLETED',
  'CONVERSATION_COMPACTION_STARTED',
  'CONVERSATION_COMPACTION_COMPLETED',
  'CONVERSATION_COMPACTION_FAILED',
]);

type StreamableGeneration = Pick<
  MessageGeneration,
  | 'content'
  | 'thinkContent'
  | 'searchSources'
  | 'isAnalyzing'
  | 'isAnalyzed'
  | 'toolCallStreamStatus'
  | 'skillStreamStatus'
  | 'subagentStreamStatus'
  | 'streamStatusEntries'
>;

/** Stream events that do not carry visible assistant text yet. */
export const shouldClearMessageLoadingForStreamEvent = (eventType: string | undefined): boolean => {
  if (!eventType) return true;
  return !METADATA_ONLY_STREAM_EVENT_TYPES.has(eventType.toUpperCase());
};

export const hasStreamingAssistantContent = (generation?: StreamableGeneration): boolean => {
  if (!generation) return false;
  if (generation?.content?.trim()) return true;
  if (generation.thinkContent?.trim()) return true;
  if (generation.searchSources?.length) return true;
  return false;
};

export const shouldShowStreamingToolCallStatus = (
  generation?: StreamableGeneration
): boolean => shouldRenderToolCallStreamStatus(generation);

export const shouldShowStreamingSkillStatus = (
  generation?: StreamableGeneration
): boolean => shouldRenderSkillStreamStatus(generation);

export const shouldShowStreamingSubagentStatus = (
  generation?: StreamableGeneration
): boolean => shouldRenderSubagentStreamStatus(generation);

export const shouldShowStreamingActivityStatus = (
  generation?: StreamableGeneration
): boolean =>
  hasStreamStatusEntries(generation) ||
  shouldShowStreamingSkillStatus(generation) ||
  shouldShowStreamingSubagentStatus(generation) ||
  shouldShowStreamingToolCallStatus(generation);

/** Keep the typing loader until the assistant bubble has something to render. */
export const shouldShowStreamingMessageLoader = (params: {
  isLoading: boolean;
  isStillGenerating: boolean;
  generation?: StreamableGeneration;
}): boolean => {
  const { isLoading, isStillGenerating, generation } = params;

  if (generation?.isAnalyzing || generation?.isAnalyzed) return false;
  if (hasStreamingAssistantContent(generation)) return false;
  if (shouldShowStreamingActivityStatus(generation)) return false;

  return isLoading || isStillGenerating;
};
